import { useState } from 'react'
import {
  UseMutationOptions,
  useMutation as useReactMutation,
} from '@tanstack/react-query'
import { AxiosError, AxiosResponse } from 'axios'
import { APIErrorHandler } from '../APIErrorHandler/APIErrorHandler'

export function useMutation<A, B>(
  api: (_: A) => Promise<AxiosResponse<B>>,
  onSuccess?: (_: B) => void,
  onError?: (_: Error, statusCode?: number) => void,
  options?: Omit<
    UseMutationOptions<B, Error, A, unknown>,
    'onSuccess' | 'onError' | 'mutationFn'
  >
) {
  const [errorMessage, setErrorMessage] = useState('')
  const [responseStatus, setResponseStatus] = useState<number>()

  const mutationFn = async (payload: A) => {
    try {
      const result = await api(payload)
      setErrorMessage('')
      setResponseStatus(result.status)

      return result.data
    } catch (_err) {
      const err = _err as AxiosError<{ error: Record<string, unknown> }>
      setResponseStatus(err.status)

      setErrorMessage(APIErrorHandler(err?.response?.data?.error))
      throw err
    }
  }

  const {
    data,
    status,
    error,
    isSuccess: success,
    mutate: apiFunc,
    mutateAsync,
    isPending,
  } = useReactMutation({
    ...options,
    mutationFn,
    onSuccess,
    onError: (err) => onError?.(err, responseStatus),
  })

  return [
    { data, status, error, errorMessage, success, isPending },
    { apiFunc, mutateAsync },
  ] as const
}
